'use client';

import { memo } from 'react';

import {
  ArrowLeft,
  ArrowRight,
  Crown,
  Flame,
  Heart,
  Laugh,
  MessageCircle,
  Smile,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import type {
  CharacterConfig,
  PersonalityTrait,
  RelationshipStatus,
} from './types';

interface Step5PersonalityProps {
  config: CharacterConfig;
  updateConfig: (updates: Partial<CharacterConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}

const personalityTraitIds: PersonalityTrait[] = [
  'confident',
  'playful',
  'dominant',
  'caring',
  'teasing',
];

const relationshipOptions: {
  id: RelationshipStatus;
  Icon: typeof Heart;
  desc: string;
}[] = [
  {
    id: 'romantic-partner',
    Icon: Heart,
    desc: 'Your loving and dedicated partner.',
  },
  {
    id: 'flirty-companion',
    Icon: Sparkles,
    desc: 'Fun, flirty and always keeps it exciting.',
  },
  {
    id: 'close-friend',
    Icon: Users,
    desc: "A loyal friend who's always there for you.",
  },
  {
    id: 'casual-chat',
    Icon: MessageCircle,
    desc: 'Casual conversations and easygoing vibes.',
  },
];

const traitIcons: Record<PersonalityTrait, typeof Crown> = {
  confident: Crown,
  playful: Smile,
  dominant: Flame,
  caring: Heart,
  teasing: Laugh,
};

const traitEnglishLabels: Record<PersonalityTrait, string> = {
  confident: 'Confident',
  playful: 'Playful',
  dominant: 'Dominant',
  caring: 'Caring',
  teasing: 'Teasing',
};

export const Step5Personality = memo(function Step5Personality({
  config,
  updateConfig,
  onBack,
  onNext,
}: Step5PersonalityProps) {
  const { t } = useTranslation('common');

  const togglePersonalityTrait = (trait: PersonalityTrait) => {
    const currentTraits = config.personalityTraits || [];
    let newTraits: PersonalityTrait[];

    if (currentTraits.includes(trait)) {
      newTraits = currentTraits.filter((tr) => tr !== trait);
    } else {
      newTraits = [...currentTraits, trait];
    }

    // Build personality string from selected traits (English labels kept
    // for prompt-building consistency; UI shows translated labels).
    const personalityText = newTraits
      .map((id) => traitEnglishLabels[id])
      .join(', ');

    updateConfig({
      personalityTraits: newTraits,
      personality: personalityText,
    });
  };

  return (
    <div className="mx-auto max-w-md px-2 sm:max-w-xl sm:px-4 md:max-w-2xl lg:px-0">
      {/* Title */}
      <div className="mb-2 text-center sm:mb-3">
        <h1 className="mb-0.5 text-lg font-bold text-white sm:mb-1 sm:text-xl md:text-2xl">
          <Trans i18nKey="common:characterCreator.titleNamePersonality" />
        </h1>
        <p className="text-[10px] text-gray-400 sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.namePersonalityDesc" />
        </p>
      </div>

      {/* Main Card */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-4 sm:rounded-2xl sm:p-6 md:p-7">
        {/* Character Name */}
        <div className="mb-3 sm:mb-4">
          <label className="mb-1.5 block text-xs font-semibold text-white sm:mb-2 sm:text-sm">
            <Trans i18nKey="common:characterCreator.characterName" />
          </label>
          <div className="relative">
            <User className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-pink-500" />
            <input
              type="text"
              value={config.name}
              onChange={(e) => updateConfig({ name: e.target.value })}
              placeholder={t('characterCreator.characterNamePlaceholder')}
              className="w-full rounded-lg border border-white/15 bg-black/50 py-2.5 pr-3.5 pl-10 text-sm text-white placeholder-gray-500 transition-colors focus:border-pink-500/50 focus:outline-none sm:rounded-xl sm:py-3"
            />
          </div>
          <p className="mt-1.5 text-[10px] text-gray-500 sm:text-xs">
            <Trans i18nKey="common:characterCreator.enterCharacterName" />
          </p>
        </div>

        {/* Personality */}
        <div className="mb-3 sm:mb-4">
          <label className="mb-1.5 block text-xs font-semibold text-white sm:mb-2 sm:text-sm">
            <Trans i18nKey="common:characterCreator.personalityLabel" />
          </label>
          <div className="relative mb-2.5">
            <Smile className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-pink-500" />
            <input
              type="text"
              value={config.personality}
              onChange={(e) => updateConfig({ personality: e.target.value })}
              placeholder={t('characterCreator.personalityPlaceholder')}
              className="w-full rounded-lg border border-white/15 bg-black/50 py-2.5 pr-3.5 pl-10 text-sm text-white placeholder-gray-500 transition-colors focus:border-pink-500/50 focus:outline-none sm:rounded-xl sm:py-3"
            />
          </div>
          <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
            {personalityTraitIds.map((traitId) => {
              const isSelected = (config.personalityTraits || []).includes(
                traitId,
              );
              return (
                <button
                  key={traitId}
                  type="button"
                  onClick={() => togglePersonalityTrait(traitId)}
                  className={`flex flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[10px] font-medium transition-all sm:flex-row sm:gap-1.5 sm:rounded-full sm:px-3 sm:text-xs ${
                    isSelected
                      ? 'border border-pink-500 bg-gradient-to-r from-pink-600 to-pink-700 text-white'
                      : 'border border-pink-500/40 bg-transparent text-white hover:border-pink-500/70'
                  }`}
                >
                  {(() => {
                    const TraitIcon = traitIcons[traitId];
                    return (
                      <TraitIcon
                        className={`h-3.5 w-3.5 ${isSelected ? 'text-white' : 'text-pink-500'}`}
                      />
                    );
                  })()}
                  {t(`characterCreator.options.personalityTrait.${traitId}`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Relationship Status */}
        <div>
          <label className="mb-1.5 block text-[11px] font-semibold text-white sm:mb-2 sm:text-sm">
            <Trans i18nKey="common:characterCreator.relationshipStatusLabel" />
          </label>
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
            {relationshipOptions.map(({ id, Icon, desc }) => {
              const isSelected = config.relationshipStatus === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => updateConfig({ relationshipStatus: id })}
                  className={`flex flex-col gap-1 rounded-xl border p-3.5 text-left transition-all sm:p-4 ${
                    isSelected
                      ? 'border-pink-500/70 bg-gradient-to-br from-pink-600/15 to-purple-600/15'
                      : 'border-white/15 bg-black/40 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected ? 'border-pink-500' : 'border-white/30'
                      }`}
                    >
                      {isSelected && (
                        <span className="h-2 w-2 rounded-full bg-pink-500" />
                      )}
                    </span>
                    <Icon
                      className={`h-6 w-6 shrink-0 ${isSelected ? 'text-pink-500' : 'text-pink-500/80'}`}
                      fill="currentColor"
                    />
                  </div>
                  <span className="mt-1.5 text-xs leading-tight font-semibold text-white sm:text-[15px]">
                    {t(`characterCreator.options.relationship.${id}`)}
                  </span>
                  <span className="text-[10px] leading-snug text-gray-400 sm:text-xs">
                    {desc}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-2 flex justify-center gap-2 sm:mt-3 sm:gap-2.5">
        <button
          type="button"
          onClick={onBack}
          className="flex shrink-0 items-center gap-1.5 rounded-xl border border-purple-400/50 bg-transparent px-6 py-3 text-sm font-semibold whitespace-nowrap text-white transition-all hover:border-white/50 hover:bg-white/5 sm:gap-2 sm:px-10"
        >
          <ArrowLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          <Trans i18nKey="common:characterCreator.backButton" />
        </button>

        <button
          type="button"
          onClick={onNext}
          disabled={!config.name.trim()}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-gradient-to-r from-pink-600 via-pink-500 to-pink-700 px-7 py-3 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-pink-500/30 transition-all hover:from-pink-700 hover:via-pink-600 hover:to-pink-800 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-12"
        >
          <Trans i18nKey="common:characterCreator.continueButton" />
          <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
        </button>
      </div>
    </div>
  );
});
